import { useState, useEffect } from 'react';
import { adminAPI, labAPI, documentAPI } from '../../lib/api';
import { 
  Flask, 
  FileText,
  User,
  MagnifyingGlass,
  Funnel,
  CaretDown,
  CaretUp,
  DownloadSimple,
  Eye,
  Image,
  FilePdf,
  File,
  Calendar,
  FolderOpen
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AdminLabsDocumentsPage() {
  const [patients, setPatients] = useState([]);
  const [allLabs, setAllLabs] = useState([]);
  const [allDocuments, setAllDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [expandedPatient, setExpandedPatient] = useState(null);
  const [activeTab, setActiveTab] = useState('labs');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Get all users who are patients
      const usersRes = await adminAPI.getUsers();
      const patientUsers = usersRes.data.filter(u => u.role === 'patient');
      setPatients(patientUsers);

      // Get all labs and documents for admin
      const [labsRes, docsRes] = await Promise.all([
        labAPI.getAllLabs().catch(() => ({ data: [] })),
        documentAPI.getAllDocuments().catch(() => ({ data: [] }))
      ]);
      
      setAllLabs(labsRes.data || []);
      setAllDocuments(docsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (filename) => {
    if (!filename) return File;
    const ext = filename.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return Image;
    if (ext === 'pdf') return FilePdf;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getPatientLabs = (patientId) => {
    return allLabs.filter(l => l.patient_id === patientId);
  };

  const getPatientDocuments = (patientId) => {
    return allDocuments.filter(d => d.patient_id === patientId);
  };

  // Filter and search patients
  const filteredPatients = patients.filter(patient => {
    const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) || 
                          patient.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (filterType === 'all') return true;
    if (filterType === 'has_labs') return getPatientLabs(patient.id).length > 0;
    if (filterType === 'has_docs') return getPatientDocuments(patient.id).length > 0;
    if (filterType === 'no_records') {
      return getPatientLabs(patient.id).length === 0 && getPatientDocuments(patient.id).length === 0;
    }
    return true;
  });

  const stats = {
    totalPatients: patients.length,
    totalLabs: allLabs.length,
    totalDocuments: allDocuments.length,
    patientsWithRecords: patients.filter(p => 
      getPatientLabs(p.id).length > 0 || getPatientDocuments(p.id).length > 0
    ).length
  };

  const handleViewFile = (url) => {
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Labs & Documents</h1>
          <p className="text-slate-500">View all patient lab results and uploaded documents</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.totalPatients}</p>
              <p className="text-sm text-slate-500">Total Patients</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Flask size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.totalLabs}</p>
              <p className="text-sm text-slate-500">Lab Results</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <FileText size={20} className="text-teal-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.totalDocuments}</p>
              <p className="text-sm text-slate-500">Documents</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <FolderOpen size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.patientsWithRecords}</p>
              <p className="text-sm text-slate-500">With Records</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-clinical p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by patient name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Funnel size={18} className="text-slate-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">All Patients</option>
                <option value="has_labs">Has Lab Results</option>
                <option value="has_docs">Has Documents</option>
                <option value="no_records">No Records</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Patient Records List */}
      <div className="card-clinical overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-slate-900">Patient Records ({filteredPatients.length})</h3>
        </div>

        {filteredPatients.length === 0 ? (
          <div className="p-8 text-center">
            <FolderOpen size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No patients found matching your criteria</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredPatients.map((patient) => {
              const patientLabs = getPatientLabs(patient.id);
              const patientDocs = getPatientDocuments(patient.id);
              const isExpanded = expandedPatient === patient.id;
              
              return (
                <div key={patient.id} data-testid={`patient-records-${patient.id}`}>
                  {/* Patient Row */}
                  <div 
                    className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => setExpandedPatient(isExpanded ? null : patient.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-slate-100 rounded-full">
                          <User size={20} className="text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{patient.first_name} {patient.last_name}</p>
                          <p className="text-sm text-slate-500">{patient.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3">
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                            <Flask size={12} /> {patientLabs.length} Labs
                          </span>
                          <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded-full flex items-center gap-1">
                            <FileText size={12} /> {patientDocs.length} Docs
                          </span>
                        </div>
                        {isExpanded ? <CaretUp size={20} className="text-slate-400" /> : <CaretDown size={20} className="text-slate-400" />}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-slate-50">
                      {/* Tabs */}
                      <div className="flex gap-2 mb-4">
                        <button
                          onClick={() => setActiveTab('labs')}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            activeTab === 'labs' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-white text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <Flask size={16} className="inline mr-1" /> Labs ({patientLabs.length})
                        </button>
                        <button
                          onClick={() => setActiveTab('documents')}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            activeTab === 'documents' 
                              ? 'bg-teal-100 text-teal-700' 
                              : 'bg-white text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <FileText size={16} className="inline mr-1" /> Documents ({patientDocs.length})
                        </button>
                      </div>

                      {/* Labs Tab */}
                      {activeTab === 'labs' && (
                        <div className="space-y-2">
                          {patientLabs.length === 0 ? (
                            <div className="p-4 bg-white rounded-lg border border-slate-200 text-center text-slate-500">
                              No lab results uploaded
                            </div>
                          ) : (
                            patientLabs.map((lab) => {
                              const FileIcon = getFileIcon(lab.file_name);
                              return (
                                <div 
                                  key={lab.id} 
                                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200"
                                >
                                  <div className="flex items-center gap-3">
                                    <FileIcon size={24} className="text-purple-500" />
                                    <div>
                                      <p className="font-medium text-slate-900">{lab.lab_type || lab.file_name || 'Lab Result'}</p>
                                      <div className="flex items-center gap-2 text-xs text-slate-500">
                                        <Calendar size={12} />
                                        {new Date(lab.uploaded_at || lab.created_at).toLocaleDateString()}
                                        {lab.file_size && <span>• {formatFileSize(lab.file_size)}</span>}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {lab.file_url && (
                                      <>
                                        <button 
                                          onClick={() => handleViewFile(lab.file_url)}
                                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                          title="View"
                                        >
                                          <Eye size={18} className="text-slate-600" />
                                        </button>
                                        <a 
                                          href={lab.file_url}
                                          download
                                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                          title="Download"
                                        >
                                          <DownloadSimple size={18} className="text-slate-600" />
                                        </a>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}

                      {/* Documents Tab */}
                      {activeTab === 'documents' && (
                        <div className="space-y-2">
                          {patientDocs.length === 0 ? (
                            <div className="p-4 bg-white rounded-lg border border-slate-200 text-center text-slate-500">
                              No documents uploaded
                            </div>
                          ) : (
                            patientDocs.map((doc) => {
                              const FileIcon = getFileIcon(doc.file_name);
                              return (
                                <div 
                                  key={doc.id} 
                                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200"
                                >
                                  <div className="flex items-center gap-3">
                                    <FileIcon size={24} className="text-teal-500" />
                                    <div>
                                      <p className="font-medium text-slate-900">
                                        {doc.document_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || doc.file_name || 'Document'}
                                      </p>
                                      <div className="flex items-center gap-2 text-xs text-slate-500">
                                        <Calendar size={12} />
                                        {new Date(doc.uploaded_at || doc.created_at).toLocaleDateString()}
                                        {doc.status && (
                                          <span className={`px-1.5 py-0.5 rounded ${
                                            doc.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                                          }`}>
                                            {doc.status}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {doc.file_url && (
                                      <>
                                        <button 
                                          onClick={() => handleViewFile(doc.file_url)}
                                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                          title="View"
                                        >
                                          <Eye size={18} className="text-slate-600" />
                                        </button>
                                        <a 
                                          href={doc.file_url}
                                          download
                                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                          title="Download"
                                        >
                                          <DownloadSimple size={18} className="text-slate-600" />
                                        </a>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
